import React, { useCallback, useRef, useState } from "react";
import { Button, CircularProgress } from "@mui/material";

/**
 * Button that disables and shows a spinner while onClick (sync or async) runs.
 * Use loadingLabel to show text next to the spinner; omit for spinner-only (e.g. Login).
 *
 * Controlled mode: pass `loading` from useAsyncAction (or parent) so sibling inputs can disable.
 */
const AsyncButton = React.forwardRef(function AsyncButton(
  {
    onClick,
    children,
    loadingLabel,
    spinnerSize = 18,
    disabled,
    loading: controlledLoading,
    sx,
    ...rest
  },
  ref
) {
  const [internalLoading, setInternalLoading] = useState(false);
  const pendingRef = useRef(false);
  const controlled = controlledLoading !== undefined;
  const loading = controlled ? controlledLoading : internalLoading;

  const handleClick = useCallback(
    async (e) => {
      if (disabled || loading) {
        return;
      }
      if (!onClick) {
        return;
      }
      if (!controlled) {
        if (pendingRef.current) {
          return;
        }
        pendingRef.current = true;
        setInternalLoading(true);
        try {
          await Promise.resolve(onClick(e));
        } finally {
          pendingRef.current = false;
          setInternalLoading(false);
        }
      } else {
        await Promise.resolve(onClick(e));
      }
    },
    [onClick, disabled, loading, controlled]
  );

  const showSpinner = loading;

  return (
    <Button
      {...rest}
      ref={ref}
      disabled={disabled || loading}
      onClick={handleClick}
      sx={sx}
    >
      {showSpinner ? (
        <>
          <CircularProgress
            size={spinnerSize}
            color="inherit"
            sx={{ mr: loadingLabel ? 1 : 0 }}
          />
          {loadingLabel ?? null}
        </>
      ) : (
        children
      )}
    </Button>
  );
});

export default AsyncButton;
